import React, { CSSProperties, SyntheticEvent, useEffect, useMemo, useState } from "react";

import { ButtonProps } from "semantic-ui-react";

import moment from "moment";

import { ADMIN_NAME_LIST, ADMIN_TAB_LIST } from "react-lib/apps/USER/CardSettingDivision";

import CardCourseList from "./CardCourseList";
import CardSummaryTrainingKPI from "./CardSummaryTrainingKPI";
import CardTrainingInfoManagementUX from "./CardTrainingInfoManagementUX";
import CardTrainingKPISetting from "./CardTrainingKPISetting";
import CardTrainingManagement from "./CardTrainingManagement";

import { TrainingSummaryType } from "./sequence/SummaryTrainingKPI";
import { TrainingAttendanceFilterType } from "./sequence/TrainingManagement";

import { mapOptions } from "../common/CommonInterface";
import { ORGANIZE_LABEL, PickedState } from "./HRMInterface";

import { formatDate } from "react-lib/utils/dateUtils";

// Types
type CardTrainingInfoManagementProps = Pick<
  PickedState,
  | "adminMenuDivisions"
  | "buttonLoadCheck"
  | "django"
  | "errorMessage"
  | "searchedItemListWithKey"
  | "selectedDivision"
> & {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: () => void) => any;
  runSequence: any;
  CourseListSequence: any;
  SummaryTrainingKPISequence: any;
  TrainingKPISettingSequence: any;
  TrainingManagementSequence: any;
  languageUX: string;
  masterData: any;
  masterOptions: any;
  searchedItemListWithKey: any;
};

type TabTypes =
  | "COURSE_LIST"
  | "SUMMARY_TRAINING_KPI"
  | "TRAINING_KPI_SETTING"
  | "TRAINING_SCHEDULE";

type Styles = Record<"courseList", CSSProperties>;

const styles: Styles = {
  courseList: { marginTop: "-1.25rem" },
};

const TAB_MENU_MAPPING = {
  // Course List
  [ADMIN_TAB_LIST["Course List"]]: "COURSE_LIST",
  // Training KPI Setting
  [ADMIN_TAB_LIST["Training KPI Setting"]]: "TRAINING_KPI_SETTING",
  // Summary Training KPI
  [ADMIN_TAB_LIST["Summary Training KPI"]]: "SUMMARY_TRAINING_KPI",
  // Training Schedule
  [ADMIN_TAB_LIST["Training Schedule"]]: "TRAINING_SCHEDULE",
} as const;

const CardTrainingInfoManagement = (props: CardTrainingInfoManagementProps) => {
  const [tab, setTab] = useState<"" | TabTypes>("");
  const [trainingScheduleFilter, setTrainingScheduleFilter] =
    useState<TrainingAttendanceFilterType>({});

  const filteredTabMenu = useMemo(() => {
    const tabs = props.adminMenuDivisions?.all[ADMIN_NAME_LIST["Training System"]]?.tabs ?? [];

    return Object.entries(TAB_MENU_MAPPING)
      .filter(([key]) => tabs.includes(key))
      .map((menu) => menu[1]);
  }, [props.adminMenuDivisions]);

  useEffect(() => {
    setTab(filteredTabMenu[0] || "");
  }, [filteredTabMenu]);

  const divisionOptions = useMemo(
    () => props.adminMenuDivisions?.all[ADMIN_NAME_LIST["Training System"]]?.divisions ?? [],
    [props.adminMenuDivisions]
  );

  const filteredDivisions = useMemo(() => {
    const divisions: Record<string, any>[] = props.masterData?.division ?? [];

    return mapOptions(divisions.filter((div) => div.type_label !== ORGANIZE_LABEL));
  }, [props.masterData?.division]);

  const handleChangeTab = (e: SyntheticEvent, data: ButtonProps) => {
    setTrainingScheduleFilter({});

    setTab(data.name);
  };

  const handleViewTrainingSchedule = (data: TrainingSummaryType) => {
    const currentFilter = props.SummaryTrainingKPISequence?.filter || {};
    const selectedDate = moment(data.created);

    const filter = {
      course_start_date_from: formatDate(selectedDate.startOf("month")),
      course_start_date_to: formatDate(selectedDate.endOf("month")),
      division_id: currentFilter.division_id,
      employee_id: data.employee,
      employee_name: data.employee_name,
    };

    setTrainingScheduleFilter(filter);
    setTab("TRAINING_SCHEDULE");
  };

  return (
    <div>
      <CardTrainingInfoManagementUX
        filteredTabMenu={filteredTabMenu}
        tab={tab}
        onChangeTab={handleChangeTab}
      />

      {tab === "COURSE_LIST" && (
        <CardCourseList
          onEvent={props.onEvent}
          setProp={props.setProp}
          runSequence={props.runSequence}
          CourseListSequence={props.CourseListSequence}
          style={styles.courseList}
          buttonLoadCheck={props.buttonLoadCheck}
          django={props.django}
          errorMessage={props.errorMessage}
          languageUX={props.languageUX}
          searchedItemListWithKey={props.searchedItemListWithKey}
          divisionOptions={filteredDivisions}
        />
      )}

      {tab === "TRAINING_KPI_SETTING" && (
        <CardTrainingKPISetting
          onEvent={props.onEvent}
          setProp={props.setProp}
          runSequence={props.runSequence}
          TrainingKPISettingSequence={props.TrainingKPISettingSequence}
          buttonLoadCheck={props.buttonLoadCheck}
          languageUX={props.languageUX}
          masterOptions={props.masterOptions}
          searchedItemListWithKey={props.searchedItemListWithKey}
          divisionOptions={divisionOptions}
        />
      )}

      {tab === "SUMMARY_TRAINING_KPI" && (
        <CardSummaryTrainingKPI
          setProp={props.setProp}
          runSequence={props.runSequence}
          SummaryTrainingKPISequence={props.SummaryTrainingKPISequence}
          buttonLoadCheck={props.buttonLoadCheck}
          errorMessage={props.errorMessage}
          divisionOptions={divisionOptions}
          onViewTrainingSchedule={handleViewTrainingSchedule}
        />
      )}

      {tab === "TRAINING_SCHEDULE" && (
        <CardTrainingManagement
          onEvent={props.onEvent}
          setProp={props.setProp}
          runSequence={props.runSequence}
          TrainingManagementSequence={props.TrainingManagementSequence}
          defaultFilter={trainingScheduleFilter}
          buttonLoadCheck={props.buttonLoadCheck}
          languageUX={props.languageUX}
          searchedItemListWithKey={props.searchedItemListWithKey}
          divisionOptions={divisionOptions}
        />
      )}
    </div>
  );
};

CardTrainingInfoManagement.displayName = "CardTrainingInfoManagement";

export default React.memo(CardTrainingInfoManagement);
